/**
 * Surgical span-edit helpers — the finest precise-editing tier.
 *
 * A surgical edit targets a sub-range inside a single text node of a leaf that
 * carries a `data-edit-key` (routed by `ara-selection-router`). The offsets are
 * captured **relative to the leaf element's `textContent`**; the backend returns
 * only the replacement text for that span; the frontend reinserts it as a pure
 * string splice on the one text node that owns the range. Every other text node
 * and all sibling elements are preserved byte-for-byte — zero structural risk.
 *
 * Sibling to `ara-section-html`'s element-scoped `spliceTarget*`: that swaps a
 * whole element; these swap a character range inside one element.
 */

export type AraSpanAnchor = {
  key: string
  start: number
  end: number
}

function cssEscape(value: string): string {
  const scope = globalThis as { CSS?: { escape?: (value: string) => string } }
  return scope.CSS?.escape ? scope.CSS.escape(value) : value
}

function leafTextNodes(root: Node): Text[] {
  const nodes: Text[] = []
  const walk = (node: Node) => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) nodes.push(child as Text)
      else walk(child)
    }
  }
  walk(root)
  return nodes
}

/**
 * Compute the char offsets of a sub-range, relative to `leaf`'s `textContent`,
 * from the DOM text node the range lives in plus its intra-node offsets. Returns
 * `null` when `textNode` is not a descendant text node of `leaf`.
 */
export function captureSpanOffsets(
  leaf: Element,
  textNode: Text,
  startOffset: number,
  endOffset: number
): { start: number; end: number } | null {
  let cumulative = 0
  for (const node of leafTextNodes(leaf)) {
    if (node === textNode) {
      const start = cumulative + Math.min(startOffset, endOffset)
      const end = cumulative + Math.max(startOffset, endOffset)
      return { start, end }
    }
    cumulative += node.data.length
  }
  return null
}

/** Pure string splice: replace `[start, end)` of `text` with `replacement`. */
export function spliceSpanText(
  text: string,
  start: number,
  end: number,
  replacement: string
): string {
  if (start < 0 || end > text.length || start > end) return text
  return text.slice(0, start) + replacement + text.slice(end)
}

function spliceSpanInElement(leaf: Element, anchor: AraSpanAnchor, replacement: string): boolean {
  let cumulative = 0
  for (const node of leafTextNodes(leaf)) {
    const nodeEnd = cumulative + node.data.length
    if (anchor.start >= cumulative && anchor.end <= nodeEnd) {
      const localStart = anchor.start - cumulative
      const localEnd = anchor.end - cumulative
      node.data = spliceSpanText(node.data, localStart, localEnd, replacement)
      return true
    }
    cumulative = nodeEnd
  }
  return false
}

/**
 * Reinsert `replacement` at the captured offsets inside the leaf resolved by
 * `anchor.key` within a single section's HTML. Only the text node that owns the
 * range is rewritten; unchanged text nodes and every sibling element survive
 * byte-identical. A missing key or a range that crosses text-node boundaries is
 * a deterministic no-op — the input HTML is returned unchanged.
 */
export function spliceSpan(
  sectionHtml: string,
  anchor: AraSpanAnchor,
  replacement: string
): string {
  if (!anchor.key) return sectionHtml
  const container = document.createElement('div')
  container.innerHTML = sectionHtml
  const leaf = container.querySelector(`[data-edit-key="${cssEscape(anchor.key)}"]`)
  if (!leaf) return sectionHtml
  if (!spliceSpanInElement(leaf, anchor, replacement)) return sectionHtml
  return container.innerHTML
}

/**
 * Resolve `anchor.key` against every section in `sectionOrder` and splice the
 * span into the first section that owns the leaf, returning the updated sections
 * and `matched: true`. When no section owns the leaf, `matched` is `false` and
 * the sections are returned untouched.
 */
export function spliceSpanAcrossSections(
  sections: Record<string, string>,
  sectionOrder: string[],
  anchor: AraSpanAnchor,
  replacement: string
): { sections: Record<string, string>; matched: boolean } {
  if (!anchor.key) return { sections, matched: false }
  for (const key of sectionOrder) {
    const sectionHtml = sections[key]
    if (!sectionHtml) continue
    const container = document.createElement('div')
    container.innerHTML = sectionHtml
    const leaf = container.querySelector(`[data-edit-key="${cssEscape(anchor.key)}"]`)
    if (!leaf) continue
    if (!spliceSpanInElement(leaf, anchor, replacement)) return { sections, matched: false }
    return { sections: { ...sections, [key]: container.innerHTML }, matched: true }
  }
  return { sections, matched: false }
}

/** Build the leaf `outerHTML` after a span splice — used for in-place iframe preview. */
export function buildSpanPreviewOuterHtml(
  outerHtml: string,
  anchor: AraSpanAnchor,
  replacement: string
): string {
  if (!outerHtml) return outerHtml
  const container = document.createElement('div')
  container.innerHTML = outerHtml
  const leaf = container.firstElementChild
  if (!leaf) return outerHtml
  if (!spliceSpanInElement(leaf, anchor, replacement)) return outerHtml
  return leaf.outerHTML
}
